import { useQuery } from "@tanstack/react-query";

// Use VITE_API_URL for production (split architecture: Vercel → Railway)
const API_URL = import.meta.env.VITE_API_URL || "";

export interface PublicMedia {
  id: string;
  publicId: string;
  resourceType: "image" | "video";
  title: string | null;
  description: string | null;
  altText: string | null;
  category: string | null;
  tags: string | null;
  width: number;
  height: number;
}

/**
 * Fetch public media by category
 * This uses the unauthenticated /api/public/media endpoint
 * 
 * @param category - Media category (portfolio, hero, service, etc.)
 * @param options - Additional query options
 * 
 * @example
 * const { data: portfolioImages } = usePublicMedia("portfolio");
 * const { data: heroImages } = usePublicMedia("hero", { featured: true });
 */
export function usePublicMedia(
  category?: string,
  options?: {
    featured?: boolean;
    resourceType?: "image" | "video";
    limit?: number;
    enabled?: boolean;
  }
) {
  const queryParams = new URLSearchParams();
  if (category) queryParams.append("category", category);
  if (options?.featured) queryParams.append("featured", "true");
  if (options?.resourceType) queryParams.append("resourceType", options.resourceType);
  if (options?.limit) queryParams.append("limit", options.limit.toString());

  const queryString = queryParams.toString();
  const url = `${API_URL}/api/public/media${queryString ? `?${queryString}` : ""}`;

  return useQuery<PublicMedia[]>({
    queryKey: ["public-media", category, options],
    queryFn: async () => {
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error("Failed to fetch media");
      }
      return res.json();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - media doesn't change often
    enabled: options?.enabled !== false,
  });
}

/**
 * Parse tags string into array
 */
export function parseTags(tags: string | null): string[] {
  if (!tags) return [];
  return tags.split(",").map((t) => t.trim()).filter(Boolean);
}

/**
 * Find media by tag
 */
export function findMediaByTag(media: PublicMedia[], tag: string): PublicMedia | undefined {
  return media.find((m) => parseTags(m.tags).includes(tag));
}

export default usePublicMedia;

